import { jsPDF } from 'jspdf';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  BorderStyle,
  Table,
  TableRow,
  TableCell,
  WidthType,
  PageBreak,
  ShadingType
} from 'docx';

const DEFAULT_DAYS = [1, 2, 3, 4, 5];

function parseJson(value, fallback) {
  if (value && typeof value === 'object') return value;

  try {
    return JSON.parse(value || '');
  } catch {
    return fallback;
  }
}

function normalizePrompts(content) {
  const source = parseJson(content?.compass_prompts, []);
  const items = Array.isArray(source) ? source : [];

  const prompts = items.map((item) => {
    if (typeof item === 'string') {
      return { title: '', description: '', prompt: item };
    }

    return {
      title: item?.title ?? '',
      description: item?.description ?? item?.desc ?? '',
      prompt: item?.prompt ?? item?.text ?? ''
    };
  }).filter((item) => item.prompt);

  if (prompts.length === 0 && content?.prompt) {
    prompts.push({
      title: content?.title ?? '',
      description: content?.session_description ?? '',
      prompt: content.prompt
    });
  }

  return prompts;
}

function getSavedAnswers(entry) {
  const entryData = parseJson(entry?.entry_data, {});
  const answers = entryData?.answers ??
    entryData?.responses ??
    entryData?.values ??
    entryData ??
    {};

  return answers && typeof answers === 'object' ? answers : {};
}

function getAnswer(answers, prompt, index) {
  const promptKey = `${prompt?.title ?? ''}|${prompt?.prompt ?? ''}`;
  const value = answers?.[index] ??
    answers?.[String(index)] ??
    answers?.[promptKey] ??
    '';

  return String(value ?? '');
}

function addWrappedText(doc, text, x, y, width, lineHeight = 6) {
  const lines = doc.splitTextToSize(String(text || ''), width);
  doc.text(lines, x, y);
  return y + (lines.length * lineHeight);
}

function addPageHeader(doc, title, runTitle, dayLabel) {
  doc.setFillColor(37, 87, 171);
  doc.rect(0, 0, 210, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(title, 15, 13);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(runTitle, 15, 20);
  doc.text(dayLabel, 195, 20, { align: 'right' });
  doc.setTextColor(34, 48, 60);
}

function safeFilename(value) {
  return String(value || 'academy-run')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'academy-run';
}

function escapeRtf(value) {
  return String(value ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/{/g, '\\{')
    .replace(/}/g, '\\}')
    .replace(/\r?\n/g, '\\line ');
}

function downloadRtf(filename, content) {
  const blob = new Blob([content], {
    type: 'application/rtf'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadStudyHallWorkbook({
  currentRun,
  user,
  content,
  entries
}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const runTitle = currentRun?.title ?? currentRun?.name ?? 'Academy Event Run';
  const studentName = user?.name ?? user?.email ?? 'Academy Student';
  const entryByDay = {};

  (entries ?? []).forEach((entry) => {
    const day = Number(entry?.day ?? 0);
    if (day >= 1 && day <= 5 && entry?.block === 'studyhall') {
      entryByDay[day] = {
        ...(entryByDay[day] ?? {}),
        ...getSavedAnswers(entry)
      };
    }
  });

  DEFAULT_DAYS.forEach((day, dayIndex) => {
    if (dayIndex > 0) doc.addPage();

    const dayContent = (content ?? []).find(
      (item) => Number(item?.day ?? 0) === day
    );
    const dayTitle = dayContent?.day_title ??
      dayContent?.section_title ??
      `Day ${day}`;
    const personaLabel = dayContent?.persona_label ?? '';
    const prompts = normalizePrompts(dayContent);
    const answers = entryByDay[day] ?? {};
    let y = 40;

    addPageHeader(doc, 'Study Hall Workbook', runTitle, `Day ${day}`);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    y = addWrappedText(doc, dayTitle, 15, y, 180, 7);

    if (personaLabel) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(37, 87, 171);
      doc.setFontSize(10);
      y = addWrappedText(doc, personaLabel, 15, y + 2, 180, 5);
      doc.setTextColor(34, 48, 60);
    }

    if (dayContent?.opening_paragraph || dayContent?.session_description) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      y = addWrappedText(
        doc,
        dayContent?.opening_paragraph ?? dayContent?.session_description,
        15,
        y + 6,
        180,
        5
      );
    }

    if (prompts.length === 0) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(10);
      doc.text('No Academy Prompts have been configured for this day.', 15, y + 10);
      return;
    }

    prompts.forEach((prompt, index) => {
      const answer = getAnswer(answers, prompt, index) || 'No response saved yet.';

      if (y > 235) {
        doc.addPage();
        addPageHeader(doc, 'Study Hall Workbook', runTitle, `Day ${day} continued`);
        y = 40;
      }

      doc.setDrawColor(195, 216, 239);
      doc.roundedRect(12, y - 5, 186, 7, 1, 1, 'S');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      y = addWrappedText(doc, prompt.title || `Prompt ${index + 1}`, 17, y, 176, 5.5);

      if (prompt.description) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        y = addWrappedText(doc, prompt.description, 17, y + 2, 176, 4.5);
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      y = addWrappedText(doc, prompt.prompt, 17, y + 5, 176, 5);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      y = addWrappedText(doc, answer, 17, y + 4, 176, 5);
      y += 11;
    });
  });

  doc.setProperties({
    title: `${runTitle} Study Hall Workbook`,
    author: studentName,
    subject: 'Academy Prompts and saved Study Hall responses'
  });

  doc.save(`${safeFilename(runTitle)}-study-hall-workbook.pdf`);
}

export function downloadStudyHallWorkbookRtf({
  currentRun,
  user,
  content,
  entries
}) {
  const runTitle = currentRun?.title ?? currentRun?.name ?? 'Academy Event Run';
  const studentName = user?.name ?? user?.email ?? 'Academy Student';
  const entryByDay = {};

  (entries ?? []).forEach((entry) => {
    const day = Number(entry?.day ?? 0);
    if (day >= 1 && day <= 5 && entry?.block === 'studyhall') {
      entryByDay[day] = {
        ...(entryByDay[day] ?? {}),
        ...getSavedAnswers(entry)
      };
    }
  });

  const sections = DEFAULT_DAYS.map((day) => {
    const dayContent = (content ?? []).find(
      (item) => Number(item?.day ?? 0) === day
    );
    const dayTitle = dayContent?.day_title ??
      dayContent?.section_title ??
      `Day ${day}`;
    const personaLabel = dayContent?.persona_label ?? '';
    const prompts = normalizePrompts(dayContent);
    const answers = entryByDay[day] ?? {};
    const promptText = prompts.length > 0
      ? prompts.map((prompt, index) => (
        `\\b\\fs24 ${escapeRtf(prompt.title || `Prompt ${index + 1}`)}\\b0\\par ` +
        (prompt.description
          ? `\\i ${escapeRtf(prompt.description)}\\i0\\par `
          : '') +
        `\\b ${escapeRtf(prompt.prompt)}\\b0\\par ` +
        `${escapeRtf(getAnswer(answers, prompt, index) || 'No response saved yet.')}\\par\\par `
      )).join('')
      : '\\i No Academy Prompts have been configured for this day.\\i0\\par ';

    return (
      `\\page \\b\\fs32 Day ${day}: ${escapeRtf(dayTitle)}\\b0\\par ` +
      (personaLabel
        ? `\\b\\cf1 ${escapeRtf(personaLabel)}\\cf0\\b0\\par `
        : '') +
      (dayContent?.opening_paragraph || dayContent?.session_description
        ? `${escapeRtf(
          dayContent?.opening_paragraph ?? dayContent?.session_description
        )}\\par\\par `
        : '') +
      promptText
    );
  }).join('');

  const rtf = (
    '{\\rtf1\\ansi\\deff0' +
    '{\\fonttbl{\\f0 Arial;}}' +
    '{\\colortbl;\\red37\\green87\\blue171;}' +
    '\\paperw12240\\paperh15840\\margl1440\\margr1440\\margt1440\\margb1440 ' +
    '\\f0\\fs24 ' +
    `\\qc\\b\\fs40 Study Hall Workbook\\b0\\par ` +
    `\\fs28 ${escapeRtf(runTitle)}\\par ` +
    `\\fs22 ${escapeRtf(studentName)}\\par\\par ` +
    '\\ql\\fs24 ' +
    sections +
    '}'
  );

  downloadRtf(
    `${safeFilename(runTitle)}-study-hall-workbook.rtf`,
    rtf
  );
}

export async function downloadStudyHallWorkbookDocx({
  currentRun,
  user,
  content,
  entries
}) {
  const runTitle = currentRun?.title ?? currentRun?.name ?? 'Academy Event Run';
  const studentName = user?.name ?? user?.email ?? 'Academy Student';
  const entryByDay = {};

  (entries ?? []).forEach((entry) => {
    const day = Number(entry?.day ?? 0);
    if (day >= 1 && day <= 5 && entry?.block === 'studyhall') {
      entryByDay[day] = {
        ...(entryByDay[day] ?? {}),
        ...getSavedAnswers(entry)
      };
    }
  });

  const docChildren = [];

  docChildren.push(
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 200, after: 100 },
      children: [
        new TextRun({
          text: 'STUDY HALL WORKBOOK',
          bold: true,
          size: 40,
          color: '2557AB',
          font: 'Arial'
        })
      ]
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 },
      children: [
        new TextRun({
          text: runTitle,
          bold: true,
          size: 26,
          color: '2D5016',
          font: 'Arial'
        })
      ]
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [
        new TextRun({
          text: `Camper: ${studentName}  |  Generated: ${new Date().toLocaleDateString()}`,
          italics: true,
          size: 20,
          color: '718096',
          font: 'Arial'
        })
      ]
    })
  );

  DEFAULT_DAYS.forEach((day, dayIndex) => {
    if (dayIndex > 0) {
      docChildren.push(
        new Paragraph({
          children: [new PageBreak()]
        })
      );
    }

    const dayContent = (content ?? []).find(
      (item) => Number(item?.day ?? 0) === day
    );
    const dayTitle = dayContent?.day_title ??
      dayContent?.section_title ??
      `Day ${day}`;
    const personaLabel = dayContent?.persona_label ?? '';
    const prompts = normalizePrompts(dayContent);
    const answers = entryByDay[day] ?? {};

    docChildren.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.NONE },
          bottom: { style: BorderStyle.SINGLE, size: 8, color: '2557AB' },
          left: { style: BorderStyle.SINGLE, size: 24, color: '2557AB' },
          right: { style: BorderStyle.NONE }
        },
        rows: [
          new TableRow({
            children: [
              new TableCell({
                shading: { type: ShadingType.CLEAR, fill: 'F4F8FF' },
                margins: { top: 140, bottom: 140, left: 200, right: 200 },
                children: [
                  new Paragraph({
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: `DAY ${day}`,
                        bold: true,
                        size: 20,
                        color: 'E87722',
                        font: 'Arial'
                      })
                    ]
                  }),
                  new Paragraph({
                    spacing: { after: 40 },
                    children: [
                      new TextRun({
                        text: personaLabel || `Day ${day}`,
                        bold: true,
                        size: 32,
                        color: '2557AB',
                        font: 'Arial'
                      })
                    ]
                  }),
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: dayTitle,
                        bold: true,
                        size: 22,
                        color: '2D5016',
                        font: 'Arial'
                      })
                    ]
                  })
                ]
              })
            ]
          })
        ]
      }),
      new Paragraph({ spacing: { after: 200 } })
    );

    if (dayContent?.opening_paragraph || dayContent?.session_description) {
      docChildren.push(
        new Paragraph({
          spacing: { before: 100, after: 240 },
          children: [
            new TextRun({
              text: dayContent?.opening_paragraph ?? dayContent?.session_description,
              italics: true,
              size: 21,
              color: '4A5568',
              font: 'Arial'
            })
          ]
        })
      );
    }

    if (prompts.length === 0) {
      docChildren.push(
        new Paragraph({
          spacing: { before: 100, after: 200 },
          children: [
            new TextRun({
              text: 'No Academy Prompts have been configured for this day.',
              italics: true,
              size: 20,
              color: 'A0AEC0',
              font: 'Arial'
            })
          ]
        })
      );
      return;
    }

    prompts.forEach((prompt, promptIndex) => {
      const answer = getAnswer(answers, prompt, promptIndex);
      const promptCellChildren = [];

      if (prompt.title) {
        promptCellChildren.push(
          new Paragraph({
            spacing: { after: 60 },
            children: [
              new TextRun({
                text: prompt.title,
                bold: true,
                size: 24,
                color: '2D5016',
                font: 'Arial'
              })
            ]
          })
        );
      }

      if (prompt.description) {
        promptCellChildren.push(
          new Paragraph({
            spacing: { after: 100 },
            children: [
              new TextRun({
                text: prompt.description,
                italics: true,
                size: 19,
                color: '4A5568',
                font: 'Arial'
              })
            ]
          })
        );
      }

      promptCellChildren.push(
        new Paragraph({
          spacing: { after: 120 },
          children: [
            new TextRun({
              text: 'PROMPT: ',
              bold: true,
              size: 20,
              color: 'E87722',
              font: 'Arial'
            }),
            new TextRun({
              text: prompt.prompt,
              bold: true,
              size: 21,
              color: '2557AB',
              font: 'Arial'
            })
          ]
        }),
        new Paragraph({
          spacing: { before: 60, after: 40 },
          children: [
            new TextRun({
              text: 'Your Saved Response:',
              bold: true,
              size: 18,
              color: '2D5016',
              font: 'Arial'
            })
          ]
        })
      );

      const answerLines = String(answer || 'No response saved yet.').split('\n');
      answerLines.forEach((line, lineIdx) => {
        promptCellChildren.push(
          new Paragraph({
            spacing: { after: lineIdx === answerLines.length - 1 ? 40 : 20 },
            children: [
              new TextRun({
                text: line || ' ',
                italics: !answer,
                size: 20,
                color: answer ? '1A202C' : 'A0AEC0',
                font: 'Arial'
              })
            ]
          })
        );
      });

      docChildren.push(
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 4, color: 'C3D8EF' },
            bottom: { style: BorderStyle.SINGLE, size: 4, color: 'C3D8EF' },
            left: { style: BorderStyle.SINGLE, size: 16, color: 'E87722' },
            right: { style: BorderStyle.SINGLE, size: 4, color: 'C3D8EF' }
          },
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  shading: { type: ShadingType.CLEAR, fill: 'FDF6EC' },
                  margins: { top: 140, bottom: 140, left: 180, right: 180 },
                  children: promptCellChildren
                })
              ]
            })
          ]
        }),
        new Paragraph({ spacing: { after: 220 } })
      );
    });
  });

  const doc = new Document({
    title: `${runTitle} Study Hall Workbook`,
    creator: studentName,
    description: 'Academy Prompts and saved Study Hall responses',
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1000,
              bottom: 1000,
              left: 1000,
              right: 1000
            }
          }
        },
        children: docChildren
      }
    ]
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(`${safeFilename(runTitle)}-study-hall-workbook.docx`, blob);
}